import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, AlertCircle, Fingerprint, Download } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { generateCertificatePdf } from "../lib/pdf";
import { Button } from "../components/ui/button";

function Row({ label, value, testid, mono }) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0.5 py-3 border-b border-slate-100 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <span
        data-testid={testid}
        className={`text-sm text-slate-900 ${mono ? "font-mono break-all" : "font-medium"} sm:text-right sm:max-w-[60%]`}
      >
        {value}
      </span>
    </div>
  );
}

export default function Certificate() {
  const { token } = useParams();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/public/certificate/${token}`)
      .then((r) => setCert(r.data))
      .catch(() => setError(true));
  }, [token]);

  if (error)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <AlertCircle className="w-10 h-10 text-slate-300 mb-3" />
        <h1 className="text-xl font-bold text-slate-900">Certificato non disponibile</h1>
        <p className="text-sm text-slate-500 mt-1">
          Il preventivo non è stato ancora firmato oppure il link non è valido.
        </p>
      </div>
    );

  if (!cert)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto" data-testid="certificate-page">
        <div className="flex items-center justify-center gap-2 mb-6">
          <BrandLogo />
        </div>

        <div className="bg-card border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-emerald-600 px-6 sm:px-8 py-7 text-white text-center">
            <ShieldCheck className="w-10 h-10 mx-auto mb-2" />
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">Certificato di Firma</h1>
            <p className="text-sm text-emerald-50 mt-1">
              Documento accettato e firmato elettronicamente
            </p>
          </div>

          <div className="p-6 sm:p-8">
            <div className="mb-6 text-center">
              <p className="font-mono text-sm text-slate-400">{cert.quote_number}</p>
              <h2 className="text-lg font-bold text-slate-900 mt-1">{cert.title}</h2>
            </div>

            {cert.signature_image && (
              <div className="flex justify-center mb-6">
                <div className="bg-card border border-slate-200 rounded-xl px-6 py-3">
                  <img
                    data-testid="certificate-signature-image"
                    src={cert.signature_image}
                    alt="Firma"
                    className="h-20 max-w-[280px] object-contain"
                  />
                </div>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 px-5 py-1">
              <Row label="Fornitore" value={cert.issuer_name} testid="certificate-issuer" />
              <Row label="Cliente" value={cert.client_name} testid="certificate-client" />
              <Row label="Firmatario" value={cert.signer_name} testid="certificate-signer-name" />
              <Row label="Email firmatario" value={cert.signer_email} testid="certificate-signer-email" />
              <Row label="Ruolo" value={cert.signer_role} testid="certificate-signer-role" />
              <Row label="Data e ora firma" value={fmtDate(cert.signed_at)} testid="certificate-signed-at" />
              <Row label="Indirizzo IP" value={cert.ip_address} testid="certificate-ip" mono />
              <Row label="Dispositivo" value={cert.user_agent} testid="certificate-ua" />
            </div>

            <div className="mt-6 rounded-xl bg-slate-900 text-white px-5 py-4">
              <div className="flex items-center gap-2 mb-2">
                <Fingerprint style={{ width: 16, height: 16 }} className="text-emerald-400" />
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                  Sigillo di integrità (SHA-256)
                </span>
              </div>
              <p data-testid="certificate-hash" className="font-mono text-[11px] sm:text-xs break-all text-emerald-300 leading-relaxed">
                {cert.document_hash}
              </p>
              <p className="text-[11px] text-slate-400 mt-2 leading-snug">
                Questo codice univoco certifica il contenuto esatto del documento firmato. Qualsiasi
                modifica al preventivo genererebbe un sigillo differente.
              </p>
            </div>

            {cert.proposal_url && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  data-testid="certificate-download-pdf"
                  onClick={() => generateCertificatePdf({ cert })}
                  className="w-full sm:w-auto font-semibold"
                >
                  <Download style={{ width: 15, height: 15 }} className="mr-1.5" /> Scarica PDF
                </Button>
                <a
                  data-testid="certificate-proposal-link"
                  href={cert.proposal_url}
                  className="text-sm font-semibold text-primary hover:underline"
                >
                  Visualizza il preventivo completo →
                </a>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Certificato generato automaticamente da MyQuoteFlow.
        </p>
      </div>
    </div>
  );
}
